import { useState, Fragment, useMemo } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import classNames from 'classnames';

import { useMainMachine } from '../../../utils/useMainMachine';
import { Button } from '../basic-ui/Button';
import { Spinner } from '../basic-ui/Spinner';
import { BUTTON_TYPES, GENERATING } from '../../constants';
import { BrandGuidelinesModal } from '../modals/BrandGuidelines';
import { MODAL_ACTIONS } from '../../../modal/modalMachine';
import { ACTIONS } from '../../../state';
import { inflect } from '../../../utils/string';

import ExpandIcon from '@crazyegginc/hatch/dist/images/icon-arrow-expand.svg?react';
import BadgeTickIcon from '@crazyegginc/hatch/dist/images/icon-badge-tick.svg?react';
import BadgeCogIcon from '@crazyegginc/hatch/dist/images/icon-badge-cog.svg?react';
import BadgeCrossIcon from '@crazyegginc/hatch/dist/images/icon-badge-cross.svg?react';

export function BrandGuidelines() {
  const { machine, useSelector } = useMainMachine();

  const canEdit = useSelector(
    ({ context }) => (context.userId && context.hasPublishPermissions) || !context.editor.user_id,
  );
  const site = useSelector(({ context }) => context.editor.pages.find((p) => p.id === context.activePageId)?.domain);
  const generating = useSelector((state) => state.hasTag(GENERATING));

  const configured = useSelector(
    ({ context }) =>
      Boolean(context.brandGuidelines?.updatedAt) &&
      context.brandGuidelines?.checks.filter((c) => c.enabled === true).length > 0,
  );
  const isLoading = useSelector(({ context }) => Object.values(context.runningCheckRefs).filter(Boolean).length > 0);
  const failedChecks = useSelector(({ context }) =>
    context.brandCheckResults?.filter(
      (e) =>
        !Object.entries(context.editor.pages.find((p) => p.id === context.activePageId).ignoredChecks).some(
          ([c, s]) => e.check_id === c && s.includes(e.selector),
        ),
    ),
  );
  const hasFailedChecks = failedChecks?.length > 0;
  const compliant = failedChecks?.length === 0;

  const { groupedChecks, sortedKeys } = useMemo(() => {
    if (!failedChecks || !failedChecks.length) return {};
    const groupedChecks = failedChecks.reduce((acc, curr) => {
      const key = curr.checkName;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(curr);
      return acc;
    }, {});
    const sortedKeys = Object.entries(groupedChecks)
      .sort(([, a], [, b]) => {
        if (b.length !== a.length) return b.length - a.length;
        return a[0].checkName.localeCompare(b[0].checkName);
      })
      .map(([key]) => key);
    return { groupedChecks, sortedKeys };
  }, [failedChecks]);

  function openModal() {
    if (!canEdit) {
      machine.send({
        type: ACTIONS.SHOW_AUTH_MODAL,
      });
      return;
    }
    machine.send({
      type: MODAL_ACTIONS.SHOW,
      component: <BrandGuidelinesModal />,
    });
  }

  if (isLoading || (generating && configured)) {
    return (
      <Button
        variant={BUTTON_TYPES.SECONDARY}
        className="!font-normal !px-2 !bg-gray-500 !text-white shrink-0"
        onClick={openModal}
        disabled={true}
      >
        <Spinner className="w-3.5 h-3.5 mr-2.5" />
        Checking guidelines...
      </Button>
    );
  }

  if (hasFailedChecks) {
    return (
      <Popover className="relative shrink-0">
        <Popover.Button as={Button} variant={BUTTON_TYPES.SECONDARY} className="!font-normal !pl-1.25">
          <div className="relative mr-2.5">
            <BadgeCrossIcon className="text-[#FF993B] w-[18px] h-[18px] fill-current z-10 relative" />
            <div className="absolute rounded-full bg-white w-3.5 h-3 top-0.5 left-0.5 z-0" />
          </div>
          Not brand compliant
        </Popover.Button>
        <Transition
          appear={true}
          as={Fragment}
          leave="transition-opacity ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          enter="transition-opacity ease-in duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-100"
        >
          <Popover.Panel className="absolute z-10 left-0 top-full mt-2 bg-gray-600 rounded-md p-2.5 shadow-md flex flex-col w-[280px]">
            {({ close }) => (
              <div className="text-gray-200">
                <div className="px-2.5 font-semibold text-base text-white">
                  {failedChecks.length} {inflect('issue', failedChecks.length)} on this page:
                </div>
                <div className="bg-gray-500 h-px -mx-2.5 mt-2.5" />
                <div className="max-h-[350px] overflow-y-auto styled-scrollbar">
                  {sortedKeys.map((checkName) => (
                    <FailedCheck
                      key={checkName}
                      failures={groupedChecks[checkName]}
                      checkName={checkName}
                      checkId={groupedChecks[checkName][0].check_id}
                      close={close}
                    />
                  ))}
                </div>
                <div className="bg-gray-500 h-px -mx-2.5 my-2.5" />
                <Button
                  variant={BUTTON_TYPES.SECONDARY}
                  className="!w-full"
                  onClick={() => {
                    openModal();
                    close();
                  }}
                >
                  View guideline settings
                </Button>
              </div>
            )}
          </Popover.Panel>
        </Transition>
      </Popover>
    );
  }

  if (!configured) {
    return (
      <Popover className="relative shrink-0">
        <Popover.Button
          as={Button}
          variant={BUTTON_TYPES.SECONDARY}
          className="!font-normal !pl-1.25"
          disabled={generating}
        >
          <BadgeCogIcon className="w-[18px] h-[18px] fill-current mr-2.5" />
          Brand Guidelines
        </Popover.Button>
        <Transition
          appear={true}
          as={Fragment}
          leave="transition-opacity ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          enter="transition-opacity ease-in duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-100"
        >
          <Popover.Panel className="absolute z-10 left-0 top-full bg-gray-600 rounded-md p-3.75 shadow-md flex flex-col w-[220px]">
            {({ close }) => (
              <div className="text-gray-200 font-semibold">
                No brand guidelines currently set for <span className="text-white">{site}</span>
                <div className="bg-gray-500 h-px -mx-3.75 my-2.5" />
                <Button
                  className="!w-full"
                  onClick={() => {
                    openModal();
                    close();
                  }}
                >
                  Set brand guidelines
                </Button>
                <Button className="!w-full mt-1" variant={BUTTON_TYPES.CANCEL} onClick={close}>
                  Ignore
                </Button>
              </div>
            )}
          </Popover.Panel>
        </Transition>
      </Popover>
    );
  }

  if (compliant) {
    return (
      <Button variant={BUTTON_TYPES.SECONDARY} className="!font-normal !pl-1.25 shrink-0" onClick={openModal}>
        <div className="relative mr-2.5">
          <BadgeTickIcon className="w-[18px] h-[18px] fill-current text-green-500 z-10 relative" />
          <div className="absolute rounded-full bg-white w-3.5 h-3 top-0.5 left-0.5 z-0" />
        </div>
        Brand compliant
      </Button>
    );
  }
  return null;
}

const storageKey = 'closedSections';

function getClosedSections() {
  let closedSections = localStorage.getItem(storageKey) ?? '[]';
  try {
    closedSections = JSON.parse(closedSections);
  } catch {
    return [];
  }
  return closedSections;
}

function addToClosedSections(checkId) {
  const closedSections = getClosedSections();
  const newValues = [...new Set([...closedSections, checkId])];

  localStorage.setItem(storageKey, JSON.stringify(newValues));
}

function removeFromClosedSections(checkId) {
  const closedSections = getClosedSections();
  const set = new Set([...closedSections]);
  set.delete(checkId);
  const newValues = [...set];

  localStorage.setItem(storageKey, JSON.stringify(newValues));
}

function FailedCheck({ failures, checkName, checkId, close }) {
  const { machine } = useMainMachine();
  const [open, setOpen] = useState(() => !getClosedSections().includes(checkId));

  function selectElement(selector) {
    machine.send({ type: ACTIONS.SELECT_FAILED_ELEMENT, selector });
    close();
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (open) {
            addToClosedSections(checkId);
          } else {
            removeFromClosedSections(checkId);
          }
          setOpen(!open);
        }}
        className="px-2.5 mt-5 w-full flex items-center justify-between text-white font-semibold"
      >
        {checkName} ({failures.length})
        <ExpandIcon
          aria-label={open ? 'close section' : 'open section'}
          className={classNames('w-3 h-3 fill-current text-gray-300 hover:text-white', {
            'rotate-180': open,
            'rotate-90': !open,
          })}
        />
      </button>
      <div className="overflow-hidden">
        <motion.div
          initial={false}
          animate={
            open
              ? {
                  height: 'auto',
                  opacity: 1,
                }
              : {
                  height: 0,
                  opacity: 0,
                }
          }
          transition={{ duration: 0.4, ease: 'linear' }}
          className="mt-2.5 flex flex-col"
          id="pages-content"
        >
          {failures.map((failure) => (
            <button
              key={failure.selector}
              type="button"
              className="h-[25px] flex items-center px-3.75 text-gray-200 hover:text-white hover:bg-gray-500 rounded-md"
              onClick={() => selectElement(failure.selector)}
            >
              <div className="truncate">{failure.text}</div>
            </button>
          ))}
        </motion.div>
      </div>
    </>
  );
}
